// Gedeelde form-submit helper: POST naar Web3Forms.
// Alle formulieren op de site gebruiken deze functie zodat:
//   1. Eén plek om te switchen van endpoint (bv. straks naar eigen worker)
//   2. Consistente foutafhandeling
//   3. Eenmalig API-key + spam-fields
//
// 2026-05-28 bulletproofing:
//   - Retry 1x bij netwerk-fout (transient connectivity / iOS Safari issue)
//   - Detailed error categorisatie (validation vs network vs API vs server)
//   - LocalStorage logging zodat we mislukte submits later kunnen debuggen
//   - Timeout van 15 sec zodat user niet eindeloos wacht
//
// Gebruik:
//   const { ok, error, errorType } = await submitForm({
//     subject: 'Offerte-aanvraag homepage',
//     fields: { naam, telefoon, postcode, ongedierte },
//   });
//   if (ok) showSuccess(); else showError(error);

import { WEB3FORMS_KEY } from './data.js';
import { getLeadSourceFields, getLeadSourceMeta } from './lead-source.js';
import { trackEvent } from './analytics.js';

// ── DEFINITIEVE OPLOSSING 2026-05-29 ────────────────────────────────
// Submissions gaan nu via /api/submit (Cloudflare Pages Function) i.p.v.
// rechtstreeks naar Web3Forms. Voordelen:
//   1. SAME-ORIGIN: geen CORS-issues, geen CSP-blokkades
//   2. Geen UA-filtering: Web3Forms ziet altijd een geldige browser-UA
//      (server-side gespoofd in /api/submit)
//   3. Werkt in iOS in-app browsers (Instagram, Facebook, Gmail, etc.)
//   4. Carrier rate-limits worden omzeild (request gaat naar eigen domein)
// Als /api/submit zelf faalt (zeer zeldzaam), proberen we Web3Forms direct
// als fallback. Daarna mailto-knop voor user.
const SUBMIT_ENDPOINT = '/api/submit';
const WEB3FORMS_ENDPOINT_FALLBACK = 'https://api.web3forms.com/submit';
const TIMEOUT_MS = 15000;
const RETRY_COUNT = 1; // 1 retry = 2 total attempts

/** Log naar localStorage zodat we mislukkingen later kunnen debuggen. */
function logAttempt(status, payload, error) {
  try {
    if (typeof localStorage === 'undefined') return;
    const key = 'abc-form-log';
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    existing.push({
      ts: new Date().toISOString(),
      status,
      subject: payload?.subject || '(geen)',
      error: error || null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '(unknown)',
    });
    // Houd alleen laatste 20 entries
    while (existing.length > 20) existing.shift();
    localStorage.setItem(key, JSON.stringify(existing));
  } catch { /* ignore */ }
}

/** Doe één POST-poging met timeout. Eerst /api/submit, dan Web3Forms direct als fallback. */
async function attemptSubmit(payload, useDirectFallback = false) {
  const endpoint = useDirectFallback ? WEB3FORMS_ENDPOINT_FALLBACK : SUBMIT_ENDPOINT;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success !== false) {
      return { ok: true, via: useDirectFallback ? 'web3forms-direct' : 'pages-function' };
    }
    return {
      ok: false,
      errorType: 'api',
      error: json.message || `HTTP ${res.status}`,
    };
  } catch (err) {
    clearTimeout(timeoutId);
    // AbortError = timeout, anders netwerk-issue
    const isTimeout = err.name === 'AbortError';
    return {
      ok: false,
      errorType: isTimeout ? 'timeout' : 'network',
      error: isTimeout ? 'Verbinding duurde te lang (>15 sec)' : (err.message || 'Netwerkfout'),
    };
  }
}

export async function submitForm({ subject, fields, formName }) {
  // Validatie: niet posten zonder key (lokaal-dev guard).
  if (!WEB3FORMS_KEY || WEB3FORMS_KEY.startsWith('REPLACE_')) {
    console.warn('[submitForm] WEB3FORMS_KEY niet gezet — submission overgeslagen');
    return { ok: false, errorType: 'config', error: 'Configuration error' };
  }

  // Payload conform Web3Forms-API: access_key + alle veld-namen
  // worden 1-op-1 doorgestuurd als email-body.
  const payload = {
    access_key: WEB3FORMS_KEY,
    subject: subject || 'Nieuwe form-submission ABC Ongediertebestrijding',
    from_name: formName || 'ABC Ongedierte website',
    // Spam-guard: web3forms negeert submission als botcheck-veld is ingevuld
    // (anti-honeypot: bots vullen elk leeg veld automatisch in).
    botcheck: '',
    ...fields,
    // Lead-attributie: bron + landingspagina + huidige pagina, zodat je in
    // de aanvraag-mail ziet waar de lead vandaan komt.
    ...getLeadSourceFields(),
  };

  // 2026-05-29 final: /api/submit (Pages Function) gebruikt nu Resend ipv
  // Web3Forms. Resend blokt geen server-side calls dus deze route werkt 100%.
  // Strategie:
  //   1. Eerste poging via /api/submit (Pages Function → Resend → mailbox)
  //   2. Retry 1x bij netwerk-fout
  //   3. Vangnet: Web3Forms direct vanaf browser als /api/submit zelf offline is

  // Eerste poging — via Pages Function (Resend). Same-origin, geen CORS issues.
  let result = await attemptSubmit(payload, false);

  // Retry bij netwerk/timeout
  if (!result.ok && (result.errorType === 'network' || result.errorType === 'timeout')) {
    console.warn('[submitForm] Pages Function eerste poging gefaald, retry...', result.error);
    await new Promise((r) => setTimeout(r, 800));
    result = await attemptSubmit(payload, false);
  }

  // Laatste vangnet: als /api/submit zelf onbereikbaar is, probeer Web3Forms direct
  if (!result.ok && (result.errorType === 'network' || result.errorType === 'timeout')) {
    console.warn('[submitForm] Pages Function onbereikbaar, fallback naar Web3Forms direct...');
    result = await attemptSubmit(payload, true);
  }

  // Log naar localStorage
  logAttempt(result.ok ? 'ok' : 'fail', payload, result.error);

  // GA4-conversie: 'generate_lead' bij succesvolle verzending. trackEvent
  // doet niets zonder cookie-consent, dus AVG-veilig. Markeer dit event in
  // GA4 als conversie (Beheer → Gebeurtenissen) om leads per kanaal te tellen.
  if (result.ok) {
    try {
      trackEvent('generate_lead', {
        form_name: formName || subject || 'website-form',
        ...getLeadSourceMeta(),
      });
    } catch { /* analytics-fout mag de submit nooit breken */ }
  }

  return result;
}

/** Bouw een mailto: link als noodfallback wanneer fetch totaal faalt. */
export function buildMailtoFallback({ subject, fields }) {
  const lines = Object.entries(fields).map(([k, v]) => `${k}: ${v || '-'}`);
  const body = [
    'Hallo,',
    '',
    'Mijn formulier-aanvraag (versturen lukte niet via de site, daarom direct mail):',
    '',
    ...lines,
    '',
    'Graag terugbellen.',
  ].join('\n');
  const params = new URLSearchParams({
    subject: subject || 'Offerte-aanvraag ABC Ongediertebestrijding',
    body,
  });
  return `mailto:info@abcongedierte.nl?${params.toString()}`;
}
